import { formatLocalDate } from '../utils/localDateTime';
import React from 'react';
import { COUNTRY_CODES } from '../common/data';
import { formatEventRiskScore, getEventRiskLabel, getEventRiskScore } from '../utils/riskEventFeed';
import { formatEventConfidenceLevel } from '../utils/eventConfidence';
import { OSINT_EVENT_NOTE } from '../../functions/src/common/riskEventPublication';

const COUNTRY_NAME_TO_ISO3 = Object.fromEntries(
    Object.entries(COUNTRY_CODES).map(([name, codes]) => [name.toUpperCase(), codes.iso3])
);

function canonicalCountry(value: any): string {
    const normalized = String(value || '').trim().toUpperCase();
    return COUNTRY_NAME_TO_ISO3[normalized] || normalized;
}

const RiskSidebar = ({ events = [], filterIso3, title, onReset, onEventClick }: any) => {
    const isGlobal = !filterIso3 || canonicalCountry(filterIso3) === 'GLOBAL';
    // 1. Logic: Filter based on country
    // ✅ Added (event: any) here to satisfy TypeScript
    const displayAlerts = events.filter((event: any) => {
        if (isGlobal) return true;
        
        const eventIso3 = event.iso3 || event.geo?.country_iso3 || event.location?.country;
        const eventCountry = event.country || event.location?.country;
        
        const targetIso3 = canonicalCountry(filterIso3);
        return canonicalCountry(eventIso3) === targetIso3 || canonicalCountry(eventCountry) === targetIso3;
    });

  return (
    <div className="bg-slate-950/90 border-l border-sky-500/30 backdrop-blur-md h-full w-80 lg:w-96 p-4 font-mono text-xs uppercase tracking-wider text-sky-400 flex flex-col">
      
      {/* HEADER */}
      <div className="border-b border-sky-500/50 pb-2 mb-4 sticky top-0 bg-slate-950/90 z-10 flex justify-between items-center">
        <div>
          <h2 className="text-sky-100 font-bold text-sm italic flex items-center gap-2">
            <span className="h-2 w-2 bg-emerald-500 rounded-full animate-pulse" />
            {title || (isGlobal ? 'GLOBAL THREAT FEED' : `INTEL: ${canonicalCountry(filterIso3)}`)}
          </h2>
          <span className="text-[9px] opacity-70">REAL-TIME SIGNAL DECODING</span>
        </div>

        {!isGlobal && (
          <button onClick={onReset} className="text-[9px] bg-sky-950 hover:bg-sky-900 px-2 py-1 rounded text-sky-400 border border-sky-500/30 transition-all font-bold">
            RESET VIEW
          </button>
        )}
      </div>

       {/* SCROLLABLE FEED */}
       <div className="flex-1 overflow-y-auto min-h-0 space-y-3 pr-1 custom-scrollbar">
        {displayAlerts.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-[10px] italic normal-case">
            {isGlobal ? 'Scanning for global threat actors...' : `No localized threats in ${filterIso3}...`}
          </div>
        ) : (
            displayAlerts.map((event: any, index: any) => {
            const isHighRisk = (getEventRiskScore(event) ?? -1) >= 60 || ['HIGH', 'VERY HIGH', 'CRITICAL'].includes(getEventRiskLabel(event) || '');
            
            return (
              <div 
                key={`${event.iso3}-${event.id || index}`} 
                onClick={() => onEventClick?.(event)}
                className={`p-3 bg-slate-900/40 border-l-2 rounded-sm cursor-pointer transition-all hover:bg-slate-800/80 group ${
                  isHighRisk ? 'border-red-600 shadow-[0_0_10px_rgba(220,38,38,0.1)]' : 'border-sky-600'
                }`}
              >
                <div className="flex justify-between mb-1 opacity-60 text-[9px]">
                  <span>{event.geo?.location_name || event.location?.name || event.iso3 || event.geo?.country_iso3 || 'GLOBAL'}</span>
                  <span>
                    {event.published_at?.seconds 
                      ? formatLocalDate(event.published_at.seconds * 1000)
                      : event.processedAt?.toMillis
                        ? formatLocalDate(event.processedAt.toMillis())
                        : event.timestamp
                          ? formatLocalDate(event.timestamp)
                          : ''}
                  </span>
                </div>
                
                <h4 className="text-sm font-medium text-slate-100 normal-case font-sans leading-tight mb-2 group-hover:text-sky-400 transition-colors">
                  {event.title || event.summary}
                </h4>

                <div className="flex justify-between items-center border-t border-slate-800/50 pt-2">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isHighRisk ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                    Risk: {formatEventRiskScore(event)}
                  </span>
                  <span className="text-[9px] text-slate-400">Confidence: {formatEventConfidenceLevel(event)}{event.vettingStatus === 'published_low_confidence' ? ' · Unverified' : ''}</span>
                  {event.actor && <span className="text-[9px] text-slate-500 italic lowercase tracking-tighter">src: {event.actor}</span>}
                </div>
                <p className="mt-2 text-[9px] text-slate-500">{event.sourceNote || OSINT_EVENT_NOTE}</p>
              </div>
            );
          })
        )}

        {/* 🛡️ SYSTEM METHODOLOGY FOOTER (Integrated inside the scroll area at the bottom) */}
        <div className="mt-8 pt-6 border-t border-sky-900/20 text-[9px] text-slate-500 font-mono space-y-4 pb-6">
            <div className="p-2 bg-sky-950/10 border border-sky-900/20 rounded">
                <div className="flex justify-between items-center mb-1">
                    <span className="uppercase font-black text-sky-500/70">Source_Protocol:</span>
                    <span className="text-slate-400 font-bold">GDELT GKG 2.0</span>
                </div>
                <p className="normal-case italic leading-tight opacity-60">
                    Weighted risk matrix utilizes GDELT telemetry, actor sentiment, and event frequency nodes.
                </p>
            </div>

            <div className="px-1 border-l border-red-900/40 pl-3">
                <span className="block text-red-900/80 font-black uppercase text-[8px] mb-1">Warning // AI Disclaimer:</span>
                <p className="normal-case italic leading-relaxed text-slate-500">
                    Analytic summaries are generated via machine-intelligence synthesis. Cross-reference with primary source intel before tactical execution.
                </p>
            </div>
        </div>
      </div> {/* End of Scrollable Feed */}
    </div> // End of Sidebar Container
  );
};

export default RiskSidebar;
